"use client";

import { useEffect, useState } from "react";

/**
 * Vertical reading-progress rail pinned to the left edge of the viewport. The
 * terracotta fill grows from the top as the reader scrolls, reaching 100% when
 * the bottom of the article meets the bottom of the viewport. Decorative but
 * exposed to assistive tech as a progressbar.
 */
export function ReadingProgress({ targetId = "blog-article" }: { targetId?: string }) {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    let raf = 0;

    const compute = () => {
      raf = 0;
      const el = document.getElementById(targetId);
      if (!el) return;
      const articleTop = el.getBoundingClientRect().top + window.scrollY;
      // scrollY at which the article's bottom reaches the viewport's bottom.
      const end = articleTop + el.offsetHeight - window.innerHeight;
      const p = end > 0 ? Math.min(1, Math.max(0, window.scrollY / end)) : 0;
      setProgress(p);
    };

    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(compute);
    };

    compute();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [targetId]);

  return (
    <div
      role="progressbar"
      aria-label="Reading progress"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(progress * 100)}
      className="pointer-events-none fixed left-0 top-0 z-40 h-screen w-[6px]"
    >
      {/* Track — visible rail */}
      <div className="absolute inset-0 bg-[var(--ink)] opacity-[0.12] dark:opacity-[0.18]" />
      {/* Fill — grows from the top, with a soft glow so it reads against any section */}
      <div
        className="absolute left-0 top-0 w-full bg-[var(--terracotta)] shadow-[0_0_10px_var(--terracotta)] transition-[height] duration-150 ease-out"
        style={{ height: `${progress * 100}%` }}
      />
    </div>
  );
}
